// Centralized routing for every ADE notification.
// Shared by: Alerts page (in-app tap), sw.js (web push click), nativePush.js (APNs tap).
//
// A notification is either the full in-app doc (with `type`, `related_user_id`, `data`)
// or a push payload (with `data.type`, `data.<extra>`). This helper normalises both.

export function getNotificationRoute(notification) {
  if (!notification) return "/app/alerts";

  // Normalise: push payloads put `type` inside `data`, in-app docs have it top-level.
  const data = notification.data || {};
  const type = notification.type || data.type;
  const userId =
    notification.related_user_id ||
    notification.related_user?.user_id ||
    notification.from_user?.user_id ||
    data.user_id ||
    data.from_user_id ||
    data.sender_id;

  switch (type) {
    case "discussion_reply":
    case "discussion_reply_direct":
    case "discussion_mention": {
      const did = data.discussion_id;
      if (!did) return "/app/home";
      const rid = data.reply_id;
      return rid ? `/app/discussion/${did}?reply=${encodeURIComponent(rid)}` : `/app/discussion/${did}`;
    }
    case "connection_request": {
      // Standard OR island_doorbell — both land on the same tab; the card renders
      // "tramite ADE Island" when source === 'island_doorbell'.
      const rid = data.request_id;
      return rid
        ? `/app/here?tab=connections&highlight=${encodeURIComponent(rid)}`
        : "/app/here?tab=connections";
    }
    case "island_doorbell": {
      // Legacy type (older notifications). Same routing as connection_request.
      const rid = data.request_id;
      return rid
        ? `/app/here?tab=connections&highlight=${encodeURIComponent(rid)}`
        : "/app/here?tab=connections";
    }
    case "connection_accepted":
      return userId ? `/app/user/${userId}` : "/app/here?tab=connections";

    case "professional_follow":
      // Un fan si è collegato al profilo Pro. Il payload user_id è il FAN (che può
      // essere un profilo Personal): apri il suo profilo utente, NON /app/pro.
      return userId ? `/app/user/${userId}` : "/app/professionals";

    case "message":
      return userId ? `/app/chat/${userId}` : "/app/messages";

    case "like":
    case "comment":
    case "tagged_in_post":
      if (data.story_id) return `/app/home?story=${data.story_id}`;
      return data.photo_id ? `/app/home?post=${data.photo_id}` : "/app/home";

    // Storie: la notifica deve aprire la storia, non il profilo
    case "story_comment":
    case "story_interaction":
    case "story_reaction":
      if (data.story_id) return `/app/home?story=${data.story_id}`;
      return userId ? `/app/user/${userId}` : "/app/home";

    case "mention": {
      if (data.mention_context === "story_reply") {
        return data.sender_id ? `/app/chat/${data.sender_id}` : "/app/alerts";
      }
      if (!data.photo_id) return "/app/alerts";
      const base = `/app/home?post=${data.photo_id}`;
      return data.comment_id ? `${base}&comment=${data.comment_id}` : base;
    }

    case "story_reaction":
      if (data.story_id) return `/app/home?story=${data.story_id}`;
      return userId ? `/app/user/${userId}` : "/app/home";

    case "gift_received":
    case "gift":
      if (data.requires_reveal) return "/app/gift-center?tab=uncover";
      if (data.photo_id) return `/app/home?post=${data.photo_id}`;
      if (data.island_id) return "/app/here?tab=island";
      return userId ? `/app/user/${userId}` : "/app/home";

    case "missed_call":
    case "incoming_call":
      return "/app/calls";

    case "creator_approved":
      return "/app/creator-dashboard";

    case "payout_completed":
      return "/app/creator-wallet";

    case "golden_ring":
    case "golden_ring_verified":
    case "golden_ring_submitted":
    case "golden_ring_additional_documents_required":
    case "golden_ring_approved":
    case "golden_ring_rejected":
    case "golden_ring_trial_started":
    case "golden_ring_trial_ending":
    case "golden_ring_subscription_active":
    case "golden_ring_subscription_cancelled":
    case "golden_ring_subscription_expired":
    case "golden_ring_payment_failed":
      return "/app/golden-ring";
    case "creator_kyc_submitted":
    case "creator_kyc_additional_documents_required":
    case "creator_kyc_verified":
    case "creator_kyc_rejected":
    case "creator_kyc_document_expiring":
    case "creator_kyc_expired":
      return "/app/creator/kyc";

    case "nearby":
      return "/app/here";

    case "mission_nearby":
    case "mission_reminder":
    case "mission_launched":
      return data.campaign_id ? `/app/missions/${data.campaign_id}` : "/app/home";
    case "mission_reward":
      return "/app/missions/history";

    case "creator_reward_available":
      return "/app/credits?tab=rewards";
    default:
      return userId ? `/app/user/${userId}` : "/app/alerts";
  }
}
